import { Component, HostBinding, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture } from '@cds/betting-offer';
import { MarqueeIndexedFixtureView } from '@cds/betting-offer/domain-specific';
import { CDNCachingConfig, MarqueeConfig } from '@frontend/sports/common/client-config-data-access';
import { MarqueeBackgroundContainerConfig, MarqueeType } from '@frontend/sports/types/components/content';
import { FixtureEnumStage, WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import { MarqueeLink, MarqueePayload } from '@frontend/sports/types/components/widget/types';
import { WidgetComponent, WidgetContextService, mergeMarqueePayload } from '@frontend/sports/widget/core/feature';
import { DsCard } from '@frontend/ui/card';
import { Observable, Subscription, filter, forkJoin, map } from 'rxjs';

import { DetailedEventViewModel } from '../event-details-common/event-details-common.models';
import { EventDetailsViewModelService } from '../event-details-common/event-details-view-model.service';
import { FixtureUpdateService } from '../racing/common/fixture-update.service';
import { Tile } from '../shared-models/tile.model';
import { AutomatedMarqueeService } from './automated-marquee.service';
import { HighlightsMarqueeComponent } from './highlights-marquee.component';
import { MarqueeResponseMapperService } from './marquee-response-mapper.service';
import { MarqueeComponentTile } from './marquee-tile.model';
import { MarqueeTrackingService } from './marquee-tracking.service';
import { MarqueeWidgetDataService } from './marquee-widget-data.service';

@Component({
    imports: [HighlightsMarqueeComponent, DsCard],
    selector: 'ms-marquee-widget',
    templateUrl: './marquee-widget.component.html',
    providers: [MarqueeTrackingService, FixtureUpdateService], // additionally registering dedicated tracking service, to make sure modular-tracking.service overrides tracking.service
})
export class MarqueeWidgetComponent extends WidgetComponent<MarqueePayload> {
    private marqueeMapper = inject(MarqueeResponseMapperService);
    private widgetConetextService = inject(WidgetContextService);
    private marqueeWidgetDataSerivce = inject(MarqueeWidgetDataService);
    private marqueeConfig = inject(MarqueeConfig);
    private automatedMarqueeService = inject(AutomatedMarqueeService);
    private cdnCachingConfig = inject(CDNCachingConfig);
    private eventDetailsModelService = inject(EventDetailsViewModelService, { optional: true });

    @HostBinding('class') className = 'marquee-widget';

    tiles: MarqueeComponentTile[] = [];
    backgroundContainerConfig: MarqueeBackgroundContainerConfig;
    globalLinkConfig: MarqueeLink;
    private detailedEventViewModel?: DetailedEventViewModel;
    private subscription?: Subscription;
    private shouldIncludePayload = false;

    constructor() {
        super();
        this.shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.Marquee);
        this.eventDetailsModelService?.detailedEventViewModel$.pipe(takeUntilDestroyed()).subscribe((data) => {
            this.detailedEventViewModel = data;
        });
    }

    protected override onData(data: MarqueePayload): void {
        if (!data?.fixtures?.length && !data?.tiles?.length) {
            forkJoin([this.getData(), this.getData(false, true)])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(([regularMarqueePayload, personalizedMarqueePayload]) => {
                    const combinedData = mergeMarqueePayload(regularMarqueePayload, personalizedMarqueePayload);
                    this.renderData(combinedData);
                });
        } else {
            this.renderData(data);
        }
    }

    renderData = (data: MarqueePayload) => {
        if (data?.fixtures?.length || data?.tiles?.length) {
            const tiles = this.marqueeMapper.mapFixtures(data.fixtures, data.tiles);
            if (this.marqueeWidgetDataSerivce.isNotPersonalizedMarquee(data)) {
                this.hasData = false;
                this.setWidgetLoaded();

                return;
            }

            if (data.showAutomatedMarqueesOnEventPage) {
                this.tiles = this.loadAutomatedMarquees(tiles);
            }

            this.backgroundContainerConfig = {
                showTitle: data.backgroundContainerConfig?.showTitle,
                title: data.backgroundContainerConfig?.title,
                titleIconDisplay: data.backgroundContainerConfig?.titleIconDisplay,
                backgroundImage: '',
                textColor: '',
                regularFixture: this.populateHeaderData(tiles, data.fixtures),
            };
            this.globalLinkConfig = {
                linkOverride: data.globalLink?.linkOverride,
                title: data.globalLink?.title,
                visible: data.globalLink?.visible ?? false,
                target: data.globalLink?.target,
            };

            this.subscription?.unsubscribe();
            this.subscription = this.marqueeWidgetDataSerivce
                .combineWithPromotions(data, tiles)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((combinedTiles) => {
                    this.tiles = combinedTiles;
                    this.hasData = this.tiles.length > 0;
                    this.setWidgetLoaded();
                });
            this.hasData = this.tiles.length > 0;
        } else {
            this.hasData = false;
            this.logEmptyData();
        }
        this.setWidgetLoaded();
    };

    override getContext(): Partial<WidgetContext> {
        return {
            ...super.getContext(),
            shouldIncludePayload: this.shouldIncludePayload,
            marqueesOnly: true,
            fixtureStage: <FixtureEnumStage | undefined>this.detailedEventViewModel?.fixture?.stage,
        };
    }

    private getData(addSchemaMarkup: boolean = false, shouldIncludePersonalizedData: boolean = false): Observable<MarqueePayload> {
        return this.widgetConetextService
            .getWidget<MarqueePayload>(
                { ...this.getContext(), shouldIncludePersonalizedData },
                addSchemaMarkup,
                false,
                this.widgetRouteSnapshot.getActiveRoute(),
            )
            .pipe(
                filter(({ payload }) => payload !== undefined || this.shouldIncludePayload),
                map(({ payload }) => (payload ?? {}) as MarqueePayload),
                takeUntilDestroyed(this.destroyRef),
            );
    }

    private populateHeaderData(tilesList: Tile[], fixturesData: MarqueeIndexedFixtureView[]): Fixture | undefined {
        const filteredTile = tilesList?.find((tile) => tile.marqueeType === MarqueeType.Regular);
        if (filteredTile) {
            const filteredFixtures = fixturesData?.find((fixture) => fixture.fixture.id === filteredTile.fixtureId);
            if (filteredFixtures) {
                return filteredFixtures.fixture;
            }
        }

        return;
    }

    private loadAutomatedMarquees(tiles: Tile[]): Tile[] {
        let marqueeTitles = tiles;

        if (this.detailedEventViewModel?.fixture && tiles.length < this.marqueeConfig.maxTiles && this.marqueeConfig.isAutomatedMarqueesEnabled) {
            const automatedMarqueeTiles = this.automatedMarqueeService.createAutomatedMarqueeTiles(this.detailedEventViewModel);
            const automatedTiles = this.automatedMarqueeService.getTiles(
                this.detailedEventViewModel.fixture,
                automatedMarqueeTiles,
                this.detailedEventViewModel.betBuilderFixture?.sourceFixture,
                this.detailedEventViewModel.betBuilderFixture?.precreatedMarketsInfo,
            );
            marqueeTitles = this.automatedMarqueeService.getCombinedTiles(marqueeTitles, automatedTiles);
        }

        return marqueeTitles;
    }
}
